import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCreateEquipment, useUpdateEquipment } from "@/hooks/use-equipment-mutations";
import { useStartEnrichment } from "@/hooks/use-equipment-enrichment";
import { useLinkEquipmentProduct } from "@/hooks/use-equipment-products";
import { checkProductFields, type ProductFields } from "@/lib/equipment/product-link";
import { EQUIPMENT_STATUSES, EQUIPMENT_STATUS_LABELS, type EquipmentStatus } from "@/lib/equipment/constants";
import { toEquipmentInput, toFormValues, type EquipmentFormValues } from "@/lib/equipment/form";
import type { DBDepartment, DBEquipmentCategory, DBEquipmentItem } from "@/lib/db-types";

interface EquipmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = registrer nytt. */
  item: DBEquipmentItem | null;
  categories: DBEquipmentCategory[];
  departments: DBDepartment[];
  defaultDepartmentId: string | null;
  onSaved?: (id: string) => void;
}

const selectCls = "h-9 w-full rounded-md border border-input bg-background px-3 text-sm";

const EMPTY_PRODUCT: ProductFields = { brand: "", model: "", productUrl: "" };

/**
 * Registrer/rediger utstyr (mockup C). Ved registrering kan merke, modell og
 * produktlenke fylles ut; da kobles utstyret til modellen og produktdata hentes
 * fra lenken (VIB-2936). For eksisterende utstyr gjøres det i «Hent produktdata».
 */
export function EquipmentFormDialog(props: EquipmentFormDialogProps) {
  const { open, item, categories, departments, defaultDepartmentId } = props;
  const [values, setValues] = useState<EquipmentFormValues>(() => toFormValues(item, categories, defaultDepartmentId));
  const [product, setProduct] = useState<ProductFields>(EMPTY_PRODUCT);
  const create = useCreateEquipment();
  const update = useUpdateEquipment();
  const link = useLinkEquipmentProduct();
  const startEnrichment = useStartEnrichment();
  const saving = create.isPending || update.isPending || link.isPending;

  useEffect(() => {
    if (open) {
      setValues(toFormValues(item, categories, defaultDepartmentId));
      setProduct(EMPTY_PRODUCT);
    }
  }, [open, item, categories, defaultDepartmentId]);

  const set = <K extends keyof EquipmentFormValues>(key: K, value: EquipmentFormValues[K]) =>
    setValues((v) => ({ ...v, [key]: value }));

  const onCategory = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    setValues((v) => ({ ...v, category_id: categoryId, bookable: category?.bookable ?? v.bookable }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = toEquipmentInput(values, categories);
    if ("error" in result) {
      toast.error(result.error);
      return;
    }
    const checked = item ? { link: null } : checkProductFields(product);
    if ("error" in checked) {
      toast.error(checked.error);
      return;
    }
    const productLink = checked.link;
    try {
      if (item) {
        await update.mutateAsync({ ids: [item.id], patch: result.input });
        toast.success("Utstyret er lagret");
        props.onSaved?.(item.id);
      } else {
        const id = await create.mutateAsync(result.input);
        if (productLink) {
          const productId = await link.mutateAsync({ equipmentId: id, ...productLink });
          if (productLink.productUrl) {
            // Hentingen tar 10–20 s og fortsetter etter at dialogen er lukket. Forslaget havner i køen på /utstyr/produktdata.
            startEnrichment.mutate(productId, { onError: (e) => toast.error(`Produktdata ble ikke hentet: ${e.message}`) });
          }
        }
        toast.success(productLink?.productUrl ? "Utstyret er registrert. Produktdata hentes fra lenken." : "Utstyret er registrert");
        props.onSaved?.(id);
      }
      props.onOpenChange(false);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const field = (id: keyof EquipmentFormValues, label: string, input: React.ReactNode) => (
    <div className="space-y-1.5">
      <Label htmlFor={`eq-${id}`}>{label}</Label>
      {input}
    </div>
  );
  const text = (id: keyof EquipmentFormValues, label: string, extra: React.InputHTMLAttributes<HTMLInputElement> = {}) =>
    field(
      id,
      label,
      <Input id={`eq-${id}`} value={values[id] as string} onChange={(e) => set(id, e.target.value as never)} {...extra} />,
    );

  return (
    <Dialog open={open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Rediger utstyr" : "Registrer utstyr"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {text("name", "Navn *", { required: true, autoFocus: true })}
          <div className="grid gap-4 sm:grid-cols-2">
            {field(
              "category_id",
              "Kategori *",
              <select id="eq-category_id" className={selectCls} value={values.category_id} onChange={(e) => onCategory(e.target.value)}>
                <option value="">Velg kategori</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>,
            )}
            {field(
              "department_id",
              "Sted *",
              <select id="eq-department_id" className={selectCls} value={values.department_id} onChange={(e) => set("department_id", e.target.value)}>
                <option value="">Velg sted</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>,
            )}
            {text("quantity", "Antall", { type: "number", min: 1, step: 1 })}
            {text("serial_numbers", "Serienummer", { placeholder: "Flere skilles med komma" })}
            {field(
              "status",
              "Status",
              <select id="eq-status" className={selectCls} value={values.status} onChange={(e) => set("status", e.target.value as EquipmentStatus)}>
                {EQUIPMENT_STATUSES.map((s) => (
                  <option key={s} value={s}>{EQUIPMENT_STATUS_LABELS[s]}</option>
                ))}
              </select>,
            )}
            {text("insured_until", "Forsikret til", { type: "date" })}
            {text("insurance_value", "Forsikringsverdi (totalt)", { inputMode: "decimal", placeholder: "kr" })}
            {text("purchase_price", "Innkjøpspris eks. mva", { inputMode: "decimal", placeholder: "kr" })}
            {text("purchase_date", "Innkjøpsdato", { type: "date" })}
            {text("rental_price_day", "Leiepris per døgn", { inputMode: "decimal", placeholder: "kr" })}
          </div>
          {field(
            "parts",
            "Deler og tilbehør",
            <Textarea id="eq-parts" rows={3} value={values.parts} onChange={(e) => set("parts", e.target.value)} placeholder="Én per linje, f.eks. «Softbox»" />,
          )}
          {!item && (
            <fieldset className="space-y-3 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">Modell (valgfritt)</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="eq-brand">Merke</Label>
                  <Input id="eq-brand" value={product.brand} onChange={(e) => setProduct((p) => ({ ...p, brand: e.target.value }))} placeholder="f.eks. Sony" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="eq-model">Modell</Label>
                  <Input id="eq-model" value={product.model} onChange={(e) => setProduct((p) => ({ ...p, model: e.target.value }))} placeholder="f.eks. Alpha A7S" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="eq-product-url">Produktlenke</Label>
                <Input id="eq-product-url" type="url" inputMode="url" value={product.productUrl} onChange={(e) => setProduct((p) => ({ ...p, productUrl: e.target.value }))} placeholder="https://… (produsentens side for akkurat denne modellen)" />
                <p className="text-xs text-muted-foreground">Med lenke hentes bilde, manual og spesifikasjoner fra siden. Du godkjenner dem på Produktdata før noe lagres.</p>
              </div>
            </fieldset>
          )}
          {text("comment", "Kommentar", { placeholder: "Vises under navnet i listen" })}
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={values.bookable} onCheckedChange={(c) => set("bookable", c === true)} />
            Kan bookes
          </label>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => props.onOpenChange(false)}>
              Avbryt
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Lagrer…" : "Lagre"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
